// /s/:slug — supporter permalink page.
//
// One stable URL per supporter. Shows their character (Three.js GLB viewer),
// display name, lifetime tip total across every streamer they back, the list
// of streamers, and every message they've left.
//
// Anon supporters get a "claim this supporter" CTA so they can link the slug
// to a real Twitch login later. The CTA is rendered but the backend claim
// flow is v2 — it currently routes to /login as a no-op so the link doesn't
// 404.

import { useParams, Link, Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { api } from "../lib/api";
import { pickCharacterForHandleSync } from "../lib/character";

interface SupporterStreamer {
  handle: string;
  display_name: string;
  avatar_url: string | null;
  total_cents: number;
}

interface SupporterMessage {
  id: string;
  streamer_handle: string;
  streamer_display_name: string;
  amount_cents: number;
  message: string | null;
  date: number;
  character_id: string | null;
}

interface SupporterResp {
  slug: string;
  kind: "user" | "anon";
  name: string;
  donor_handle: string;
  character_id: string | null;
  claimed: boolean;
  total_cents: number;
  donation_count: number;
  streamer_count: number;
  streamers: SupporterStreamer[];
  messages: SupporterMessage[];
}

function fmtUsd(cents: number): string {
  return `$${(cents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function fmtDate(ts: number): string {
  return new Date(ts).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export default function Supporter(): JSX.Element {
  const { slug = "" } = useParams<{ slug: string }>();

  const q = useQuery({
    queryKey: ["supporter", slug],
    queryFn: () => api.get<SupporterResp>(`/api/supporter/${encodeURIComponent(slug)}`),
    enabled: slug.length > 0,
    retry: 0,
  });

  if (!slug) return <Navigate to="/" replace />;

  if (q.isLoading) {
    return (
      <div style={{ padding: 48, fontFamily: "var(--f-mono)", color: "var(--text-mute)" }}>
        LOADING SUPPORTER…
      </div>
    );
  }
  if (q.isError || !q.data) {
    return (
      <div style={{ padding: 48 }}>
        <h1 style={{ fontFamily: "var(--f-mono)" }}>SUPPORTER NOT FOUND</h1>
        <p>No supporter lives at <code>/s/{slug}</code>.</p>
        <p><Link to="/">← Back home</Link></p>
      </div>
    );
  }

  const s = q.data;
  return <SupporterView data={s} />;
}

function SupporterView({ data }: { data: SupporterResp }): JSX.Element {
  // Resolve the GLB path. Prefer the server-supplied character_id; fall back
  // to the deterministic hash of the donor handle (same algo the donor flow
  // uses to pick a character before the first donation lands).
  const characterPath = useMemo(() => {
    if (data.character_id) {
      const group = data.character_id.startsWith("character-") ? "mini-characters" : "cube-pets";
      return `/overlay-demo/assets/${group}/${data.character_id}.glb`;
    }
    return pickCharacterForHandleSync(data.donor_handle).path;
  }, [data.character_id, data.donor_handle]);

  return (
    <div style={{ maxWidth: 1080, margin: "0 auto", padding: "32px 20px 80px" }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(240px, 360px) 1fr",
          gap: 32,
          alignItems: "start",
        }}
      >
        <CharacterView path={characterPath} />
        <div>
          <div
            style={{
              fontFamily: "var(--f-mono)",
              fontSize: 12,
              letterSpacing: "0.16em",
              color: "var(--text-mute, #9a9aa3)",
              textTransform: "uppercase",
            }}
          >
            {data.kind === "user" ? "Signed-in supporter" : "Anonymous supporter"}
          </div>
          <h1 style={{ margin: "6px 0 4px", fontSize: 48, fontWeight: 800 }}>{data.name}</h1>
          <div
            style={{
              fontFamily: "var(--f-mono)",
              fontSize: 14,
              color: "var(--text-mute, #9a9aa3)",
            }}
          >
            subswall.com/s/{data.slug}
          </div>

          <div style={{ display: "flex", gap: 32, marginTop: 24, flexWrap: "wrap" }}>
            <Stat label="LIFETIME TIPPED" value={fmtUsd(data.total_cents)} gold />
            <Stat label="TOTEMS DROPPED" value={data.donation_count.toString()} />
            <Stat label="STREAMERS BACKED" value={data.streamer_count.toString()} />
          </div>

          {(data as { imagen_url?: string | null }).imagen_url ? (
            <div
              title="This character was AI-generated from a donor prompt. EU AI Act disclosure."
              style={{
                marginTop: 16,
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "4px 10px",
                borderRadius: 12,
                background: "rgba(255, 180, 0, 0.1)",
                border: "1px solid rgba(255, 180, 0, 0.4)",
                color: "#ffb400",
                fontFamily: "var(--f-mono)",
                fontSize: 11,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                fontWeight: 700,
              }}
            >
              <span style={{ fontSize: 13 }}>⌬</span> AI-generated character
            </div>
          ) : null}

          {!data.claimed ? (
            <div
              style={{
                marginTop: 24,
                padding: 16,
                border: "1px solid var(--border, #2a2a3d)",
                borderRadius: 12,
                background: "rgba(145, 70, 255, 0.08)",
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: 6 }}>Is this you?</div>
              <div style={{ fontSize: 14, color: "var(--text-sec, #b9b9c4)", marginBottom: 12 }}>
                Claim this supporter to link it to your Twitch login and carry your wall presence
                across every streamer you back.
              </div>
              <Link
                to={`/login?claim=${encodeURIComponent(data.slug)}`}
                style={{
                  display: "inline-block",
                  padding: "8px 14px",
                  borderRadius: 8,
                  background: "#9146FF",
                  color: "#fff",
                  fontWeight: 700,
                  textDecoration: "none",
                  fontSize: 14,
                }}
              >
                CLAIM THIS SUPPORTER ▶
              </Link>
            </div>
          ) : null}
        </div>
      </div>

      {data.streamers.length > 0 ? (
        <section style={{ marginTop: 48 }}>
          <h2 style={{ fontFamily: "var(--f-mono)", letterSpacing: "0.12em" }}>
            BACKS · {data.streamers.length} STREAMER{data.streamers.length === 1 ? "" : "S"}
          </h2>
          <div style={{ display: "grid", gap: 8, marginTop: 12 }}>
            {data.streamers.map((s) => (
              <Link
                key={s.handle}
                to={`/${s.handle}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "10px 14px",
                  border: "1px solid var(--border, #2a2a3d)",
                  borderRadius: 10,
                  textDecoration: "none",
                  color: "inherit",
                  background: "var(--surface, #16161f)",
                }}
              >
                {s.avatar_url ? (
                  <img
                    src={s.avatar_url}
                    alt=""
                    style={{ width: 32, height: 32, borderRadius: "50%" }}
                  />
                ) : (
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      background: "var(--border, #2a2a3d)",
                    }}
                  />
                )}
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700 }}>{s.display_name}</div>
                  <div
                    style={{
                      fontFamily: "var(--f-mono)",
                      fontSize: 12,
                      color: "var(--text-mute, #9a9aa3)",
                    }}
                  >
                    @{s.handle}
                  </div>
                </div>
                <div style={{ fontWeight: 800, color: "#FFD700" }}>{fmtUsd(s.total_cents)}</div>
              </Link>
            ))}
          </div>
        </section>
      ) : null}

      {data.messages.length > 0 ? (
        <section style={{ marginTop: 48 }}>
          <h2 style={{ fontFamily: "var(--f-mono)", letterSpacing: "0.12em" }}>
            MESSAGES · {data.messages.length}
          </h2>
          <div style={{ display: "grid", gap: 8, marginTop: 12 }}>
            {[...data.messages]
              .sort((a, b) => a.date - b.date)
              .map((m) => (
                <div
                  key={m.id}
                  style={{
                    padding: "10px 14px",
                    border: "1px solid var(--border, #2a2a3d)",
                    borderRadius: 10,
                    background: "var(--surface, #16161f)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 12,
                      fontSize: 13,
                    }}
                  >
                    <Link
                      to={`/${m.streamer_handle}`}
                      style={{ color: "var(--text-sec, #b9b9c4)", textDecoration: "none" }}
                    >
                      → {m.streamer_display_name}
                    </Link>
                    <span
                      style={{
                        fontFamily: "var(--f-mono)",
                        color: "var(--text-mute, #9a9aa3)",
                      }}
                    >
                      {fmtDate(m.date)} · <b style={{ color: "#FFD700" }}>{fmtUsd(m.amount_cents)}</b>
                    </span>
                  </div>
                  {m.message ? (
                    <div style={{ marginTop: 6, fontSize: 15 }}>“{m.message}”</div>
                  ) : null}
                </div>
              ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}

function Stat({
  label,
  value,
  gold,
}: {
  label: string;
  value: string;
  gold?: boolean;
}): JSX.Element {
  return (
    <div>
      <div
        style={{
          fontSize: 32,
          fontWeight: 800,
          color: gold ? "#FFD700" : "inherit",
          lineHeight: 1.1,
        }}
      >
        {value}
      </div>
      <div
        style={{
          fontFamily: "var(--f-mono)",
          fontSize: 11,
          letterSpacing: "0.14em",
          color: "var(--text-mute, #9a9aa3)",
        }}
      >
        {label}
      </div>
    </div>
  );
}

// Lightweight single-character GLB viewer. Mirrors ForeverWall3D's setup but
// stripped down: one model, slow auto-rotate, no animation timeline, no
// interaction. Kept inline because nothing else needs it yet.
function CharacterView({ path }: { path: string }): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const W = canvas.clientWidth || 320;
    const H = canvas.clientHeight || 320;

    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(W, H, false);
    renderer.setClearColor(0x000000, 0);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(35, W / H, 0.1, 100);
    camera.position.set(0, 2.0, 6);
    camera.lookAt(0, 1.0, 0);

    scene.add(new THREE.AmbientLight(0xffffff, 0.85));
    const sun = new THREE.DirectionalLight(0xffffff, 0.95);
    sun.position.set(4, 8, 5);
    scene.add(sun);
    const rim = new THREE.DirectionalLight(0xffb400, 0.45);
    rim.position.set(-5, 3, -4);
    scene.add(rim);

    const ground = new THREE.Mesh(
      new THREE.CircleGeometry(2.4, 48),
      new THREE.MeshStandardMaterial({ color: 0x16161f, roughness: 0.95 }),
    );
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = 0;
    scene.add(ground);

    const pivot = new THREE.Group();
    scene.add(pivot);

    let disposed = false;
    let mixer: THREE.AnimationMixer | null = null;
    const clock = new THREE.Clock();

    const loader = new GLTFLoader();
    loader.load(
      encodeURI(path),
      (gltf) => {
        if (disposed) return;
        const root = gltf.scene;
        // Normalise scale so any GLB fits the frame.
        const box = new THREE.Box3().setFromObject(root);
        const size = box.getSize(new THREE.Vector3());
        const targetH = 2.0;
        const s = targetH / Math.max(0.001, size.y);
        root.scale.setScalar(s);
        // Drop to the floor.
        const box2 = new THREE.Box3().setFromObject(root);
        root.position.y -= box2.min.y;
        pivot.add(root);

        if (gltf.animations.length > 0) {
          mixer = new THREE.AnimationMixer(root);
          const idle =
            gltf.animations.find((c) => /idle|stand/i.test(c.name)) ?? gltf.animations[0];
          if (idle) mixer.clipAction(idle).play();
        }
      },
      undefined,
      () => {
        // Silent fail — we still render the empty stage so the page doesn't
        // look broken. Same pattern as ForeverWall3D.
      },
    );

    const tick = (): void => {
      if (disposed) return;
      requestAnimationFrame(tick);
      const dt = clock.getDelta();
      pivot.rotation.y += dt * 0.6;
      mixer?.update(dt);
      renderer.render(scene, camera);
    };
    tick();

    const ro = new ResizeObserver(() => {
      const w = canvas.clientWidth || W;
      const h = canvas.clientHeight || H;
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    });
    ro.observe(canvas);

    return () => {
      disposed = true;
      ro.disconnect();
      renderer.dispose();
    };
  }, [path]);

  return (
    <div
      style={{
        aspectRatio: "1 / 1",
        width: "100%",
        maxWidth: 360,
        background: "linear-gradient(135deg, #0E0E10 0%, #1a1426 100%)",
        border: "1px solid var(--border, #2a2a3d)",
        borderRadius: 16,
        overflow: "hidden",
      }}
    >
      <canvas ref={canvasRef} style={{ width: "100%", height: "100%", display: "block" }} />
    </div>
  );
}
